import { ReactElement, useState, KeyboardEvent, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { ClearEditorPlugin } from "@twilio-paste/core/lexical-library";
import {
  ChatComposerPlugins,
  CompositeChatComposer,
} from "@twiliointernal/e2-commons-shared-ui";
import {
  SendMessageFormValues,
  dataTestIds,
} from "@twiliointernal/e2-commons-shared-definitions";
import { LiveChatComposerProps, copy } from "./definitions";

import AttachmentsPlugin from "./sub/attachments";
import { Box } from "@twilio-paste/core/box";
import { styled } from "@twilio-paste/core/styling-library";
import { useChatService } from "./hooks/useChatService";

const DEFAULT_LENGTH = 3;

const ChatContainer = styled(Box)<{ customStyles?: string }>`
  ${({ customStyles }) => (customStyles ? customStyles : "")}
`;

const ChatComposer = (_props: LiveChatComposerProps): ReactElement => {
  const { handleSubmit, control, formState } = useForm<SendMessageFormValues>();
  const { isValid, isSubmitting } = formState;
  const [minLength, setMinLength] = useState(DEFAULT_LENGTH);
  const [filesToSend, setFilesToSend] = useState<File[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { isChatActive, sendChatMessage, notifyStartTyping, onChatError } =
    useChatService();

  const formHandler = (data: SendMessageFormValues): void => {
    setDisabled(true);
    setIsSending(true);

    sendChatMessage({
      body: data.input,
      files: filesToSend,
    })
      .then(() => {
        setFilesToSend([]);
      })
      .catch((err: Error) => {
        onChatError(err);
      })
      .finally(() => {
        setDisabled(false);
        setIsSending(false);
        setMinLength(DEFAULT_LENGTH);
      });
  };

  useEffect(() => {
    setDisabled(!isChatActive);
    setMinLength(filesToSend.length === 0 ? DEFAULT_LENGTH : 0);
  }, [filesToSend, isChatActive]);

  const reportTyping = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter") {
      notifyStartTyping();
    }
  };

  const onAttachmentSelected = (file: File): void => {
    setFilesToSend([...filesToSend, file]);
  };
  const onDeleteAttachment = (file: File): void => {
    setFilesToSend(filesToSend.filter((f) => f !== file));
  };

  const chatComposerStyles = `
  div[data-paste-element=CHAT_COMPOSER],
  div[data-paste-element=CHAT_COMPOSER]:hover {
    background-color: white;
  }`;

  return (
    <ChatContainer customStyles={chatComposerStyles}>
      <Controller
        name={"input"}
        control={control}
        rules={{ required: filesToSend.length === 0, minLength }}
        render={({ field }) => (
          <CompositeChatComposer
            config={{
              namespace: "live-chat-message",
              onError: onChatError,
            }}
            disabled={disabled}
            ariaLabel={copy.input.ariaLabel}
            placeholder={copy.input.placeholder}
            required={filesToSend.length === 0}
            {...field}
            onKeyDown={reportTyping}
            data-testid={dataTestIds.liveChat.chatComposer}
          >
            <ClearEditorPlugin />
            <ChatComposerPlugins.ChatSendButtonPlugin
              loading={isSubmitting || isSending}
              isChatActive={isChatActive}
              shouldClearInput={isValid && isSubmitting}
              disabled={!isChatActive}
              handleOnEnter={handleSubmit(formHandler)}
              handleOnClick={handleSubmit(formHandler)}
            />
            <AttachmentsPlugin
              onAttachmentSelected={onAttachmentSelected}
              onDeleteAttachment={onDeleteAttachment}
              onError={onChatError}
              disabled={!isChatActive && disabled}
              isSending={isSending}
              files={filesToSend}
            />
          </CompositeChatComposer>
        )}
      />
    </ChatContainer>
  );
};

export default ChatComposer;
